import { Container, IDestroyOptions, Point, Sprite, Texture } from "pixi.js";
import { Language } from "../../localization/Localization";
import { nameof } from "bindable-data";
import { HBox } from "./HBox";
import { CommonResourcesPackage } from "../resources/CommonResourcesPackage";
import { Event } from "event-system";
import { SettingsModel } from "../models/SettingsModel";
import { LanguageButton } from "./LanguageButton";

export class LanguagesBox extends Container {
    private static readonly BUTTONS_OFFSET = new Point(15, 26);
    private static readonly MAX_ITEMS = 5;

    public readonly onLanguageClick: Event<Language>;

    private readonly background: Sprite;
    private readonly buttons: Map<Language, LanguageButton>;

    private selectedButton: LanguageButton;

    public constructor(
        private readonly commonResources: CommonResourcesPackage,
        private readonly settingsModel: SettingsModel
    ) {
        super();
        this.onLanguageClick = new Event();

        this.background = new Sprite(this.commonResources.assets_common_languages_background_png)
        this.buttons = new Map();

        const languages = Object.values(Language);
        const boxes: Array<HBox> = [];

        for (let i = 0; i < languages.length; i++) {
            const language = languages[i];
            let box = boxes[Math.floor(i / LanguagesBox.MAX_ITEMS)];
            if (box == null) {
                box = new HBox(LanguagesBox.BUTTONS_OFFSET.x);
                boxes.push(box);
            }

            const texture = this.commonResources[this.getLanguageButtonTextureKey(language)] as Texture;
            const languageButton = new LanguageButton({ defaultTexture: texture });
            languageButton.onUp.add(() => this.onLanguageButtonClick(language));


            this.buttons.set(language, this.addChild(languageButton));
            box.addChild(languageButton);

            if (this.settingsModel.language.value == language) {
                languageButton.isSelected = true;
                this.selectedButton = languageButton;
            } else {
                languageButton.defaultView.tint = "#8c8c8c"
                languageButton.isSelected = false;
            }
        }

        for (let i = 0; i < boxes.length; i++) {
            const box = boxes[i];
            const previousBox = boxes[i - 1];
            const y = previousBox == null ? 53 : previousBox.position.y + previousBox.height + LanguagesBox.BUTTONS_OFFSET.y;
            box.position.set((this.background.width - box.width) * 0.5, y);
            this.background.addChild(box);
        }
        this.addChild(this.background);

        this.settingsModel.propertyChanged.add(this.onSettingsModelPropertyChanged, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.settingsModel.propertyChanged.remove(this.onSettingsModelPropertyChanged, this);
        for (const [_language, button] of this.buttons) {
            button.onUp.removeAll();
        }
        super.destroy(options);
    }

    private onLanguageButtonClick(language: Language): void {
        this.onLanguageClick.emit(language);
    }

    private onSettingsModelPropertyChanged(model: SettingsModel, key: string): void {
        if (key != nameof(model, "language")) {
            return;
        }
        this.selectedButton.isSelected = false;
        this.selectedButton = this.buttons.get(model.language.value);
        this.selectedButton.isSelected = true;
    }

    private getLanguageButtonTextureKey(language: Language): keyof CommonResourcesPackage {
        return `assets_common_language_icons_${language}_png` as keyof CommonResourcesPackage;
    }
}
